import React, { useState } from "react";
import { Settings, Building2, Store, Globe, Moon, Sun, Shield, Check } from "lucide-react";
import { useTheme } from "../../app/context/ThemeContext";
import { useI18n } from "../../i18n/i18n";
import { useNotification } from "../../app/context/NotificationContext";

export function SettingsModule() {
  const { theme, toggleTheme } = useTheme();
  const { lang, changeLanguage } = useI18n();
  const { addToast } = useNotification();

  const [businessProfile, setBusinessProfile] = useState({
    storeName: "Kirana Store Inventory Enterprise (Hyderabad)",
    tagline: "One system. Every product. Every location. Real-time.",
    gstin: "36AABCK9928M1Z8",
    pan: "AABCK9928M",
    fssaiLicense: "11521018000452",
    contactEmail: "admin@kiranastore.com",
    contactPhone: "+91 98490 88990",
    registeredAddress: "Door No. 4-1-898, Tilak Road, Near Abids GPO, Hyderabad 500001, Telangana",
    defaultGstSlab: "5% Standard Kirana FMCG"
  });

  const handleSave = (e) => {
    e.preventDefault();
    addToast("Business settings & tax slabs updated!", "success");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
          <Settings className="w-5 h-5 text-emerald-500" />
          Enterprise Business Configuration & Settings
        </h2>
        <p className="text-xs text-slate-500">
          Commercial entity registration, GST tax slabs, multi-lingual localization, and theme preferences
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6 text-xs">
        {/* Business Profile */}
        <div className="rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display border-b border-slate-100 dark:border-slate-800 pb-2">
            Business Profile & Tax Identifiers
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Enterprise Business Name
              </label>
              <input
                type="text"
                value={businessProfile.storeName}
                onChange={(e) => setBusinessProfile({ ...businessProfile, storeName: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                GSTIN Number (Goods & Services Tax)
              </label>
              <input
                type="text"
                value={businessProfile.gstin}
                onChange={(e) => setBusinessProfile({ ...businessProfile, gstin: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono uppercase"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                FSSAI Food License No.
              </label>
              <input
                type="text"
                value={businessProfile.fssaiLicense}
                onChange={(e) => setBusinessProfile({ ...businessProfile, fssaiLicense: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Official Support Phone
              </label>
              <input
                type="text"
                value={businessProfile.contactPhone}
                onChange={(e) => setBusinessProfile({ ...businessProfile, contactPhone: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Registered Commercial Address
              </label>
              <input
                type="text"
                value={businessProfile.registeredAddress}
                onChange={(e) => setBusinessProfile({ ...businessProfile, registeredAddress: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Localization & Preferences */}
        <div className="rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display border-b border-slate-100 dark:border-slate-800 pb-2">
            Localization & Display Theme
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Default UI Language (i18n Readiness)
              </label>
              <div className="flex items-center gap-2">
                {[
                  { code: "en", label: "English" },
                  { code: "hi", label: "हिंदी (Hindi)" },
                  { code: "mr", label: "मराठी (Marathi)" }
                ].map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => changeLanguage(l.code)}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition ${
                      lang === l.code
                        ? "bg-emerald-600 text-white border-emerald-600 shadow"
                        : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Interface Color Scheme
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="px-4 py-1.5 rounded-xl border bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold flex items-center gap-2"
                >
                  {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
                  <span>Active Theme: {theme === "dark" ? "Enterprise Dark" : "Clean Light"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/30 text-xs transition"
          >
            Save All Settings
          </button>
        </div>
      </form>
    </div>
  );
}
